import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card, CardContent } from "design-system/molecules/Card"
import { Combobox } from "design-system/organisms/Combobox"
import { isFilled } from "lopermut"
import { Import, Trash2 } from "lucide-react"
import { useShopifyLineItemPropertyKeysQuery } from "modules/settings/hooks/shopify/useShopifyLineItemPropertyKeysQuery"
import { useShopifyMetafieldDefinitionsQuery } from "modules/settings/hooks/shopify/useShopifyMetafieldDefinitionsQuery"
import { useShopifyOrderTagsQuery } from "modules/settings/hooks/shopify/useShopifyOrderTagsQuery"
import { useShopifyProductTagsQuery } from "modules/settings/hooks/shopify/useShopifyProductTagsQuery"
import { ReactElement, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  SHOPIFY_METAFIELD_MAX_LENGTH,
  ShopifyMetafieldOwnerType,
  ShopifyMetafieldsConfig,
} from "schema"

export type MetafieldGroup = keyof ShopifyMetafieldsConfig
export type MetafieldEntry = ShopifyMetafieldsConfig["product"][number]

type ComboboxOption = {
  value: string
  label: string | ReactElement
}

type MetafieldSource = "definitions" | "productTags" | "orderTags" | "lineItemProperties"

type GroupConfig = {
  kind: "metafield" | "tag" | "property"
  ownerType: ShopifyMetafieldOwnerType | null
  source: MetafieldSource
  placeholderKey: "selectMetafield" | "selectTag" | "selectProperty"
  helperTextKey?: "lineItemPropertyHint" | "productTagHint" | "orderTagHint"
  customValueLabelKey?: "useCustomTag" | "useCustomProperty"
}

type ShopifyMetafieldDefinition = NonNullable<
  ReturnType<typeof useShopifyMetafieldDefinitionsQuery>["data"]
>[number]

const TRANSLATION_PREFIX = "settings.integrations.shopify.metafields"

const GROUP_CONFIG_BY_GROUP = {
  product: {
    kind: "metafield",
    ownerType: "product",
    source: "definitions",
    placeholderKey: "selectMetafield",
    helperTextKey: undefined,
    customValueLabelKey: undefined,
  },
  productVariant: {
    kind: "metafield",
    ownerType: "productVariant",
    source: "definitions",
    placeholderKey: "selectMetafield",
    helperTextKey: undefined,
    customValueLabelKey: undefined,
  },
  order: {
    kind: "metafield",
    ownerType: "order",
    source: "definitions",
    placeholderKey: "selectMetafield",
    helperTextKey: undefined,
    customValueLabelKey: undefined,
  },
  productTags: {
    kind: "tag",
    ownerType: null,
    source: "productTags",
    placeholderKey: "selectTag",
    helperTextKey: "productTagHint",
    customValueLabelKey: "useCustomTag",
  },
  orderTags: {
    kind: "tag",
    ownerType: null,
    source: "orderTags",
    placeholderKey: "selectTag",
    helperTextKey: "orderTagHint",
    customValueLabelKey: "useCustomTag",
  },
  lineItemProperty: {
    kind: "property",
    ownerType: null,
    source: "lineItemProperties",
    placeholderKey: "selectProperty",
    helperTextKey: "lineItemPropertyHint",
    customValueLabelKey: "useCustomProperty",
  },
} satisfies Record<MetafieldGroup, GroupConfig>

function normalizeSearchValue(value: string) {
  return value.trim().toLowerCase()
}

function matchesSearchValue(value: string, searchQuery: string) {
  return normalizeSearchValue(value).includes(searchQuery)
}

function isKeyAvailable(key: string, entryKey: string, usedKeys: Set<string>) {
  return key === entryKey || !usedKeys.has(key)
}

function matchesAnySearchValue(values: string[], searchQuery: string) {
  if (!searchQuery) return true
  return values.some((value) => matchesSearchValue(value, searchQuery))
}

function buildStringOptions(
  values: string[],
  entryKey: string,
  usedKeys: Set<string>,
  searchQuery: string,
) {
  return values
    .filter(
      (value) =>
        isKeyAvailable(value, entryKey, usedKeys) && matchesAnySearchValue([value], searchQuery),
    )
    .map((value) => ({ value, label: value }))
}

function getMetafieldOptionLabel(definition: ShopifyMetafieldDefinition) {
  const text = isFilled(definition.name) ? definition.name : definition.key
  const hint = isFilled(definition.name) ? definition.key : undefined

  return (
    <span className="font-medium">
      {text}
      {hint && (
        <span className="font-normal">
          {" ("}
          {hint}
          {")"}
        </span>
      )}
    </span>
  )
}

function buildDefinitionOptions(
  definitions: ShopifyMetafieldDefinition[],
  entryKey: string,
  usedKeys: Set<string>,
  searchQuery: string,
) {
  return definitions
    .filter(
      (definition) =>
        isKeyAvailable(definition.key, entryKey, usedKeys) &&
        matchesAnySearchValue([definition.name, definition.key].filter(isFilled), searchQuery),
    )
    .map((definition) => ({
      value: definition.key,
      label: getMetafieldOptionLabel(definition),
    }))
}

function getStringValue(key: string) {
  if (!key) return undefined
  return { value: key, label: key }
}

function getDefinitionValue(definition: ShopifyMetafieldDefinition | undefined, key: string) {
  if (!key) return undefined
  if (!definition) return getStringValue(key)

  return {
    value: definition.key,
    label: isFilled(definition.name) ? `${definition.name} (${definition.key})` : definition.key,
  }
}

type Props = {
  group: MetafieldGroup
  entry: MetafieldEntry
  usedKeys: Set<string>
  onChange: (patch: Partial<MetafieldEntry>) => void
  onRemove: () => void
  onBlur: () => void
}

export const MetafieldRow = ({ group, entry, usedKeys, onChange, onRemove, onBlur }: Props) => {
  const { t } = useTranslation()
  const groupConfig = GROUP_CONFIG_BY_GROUP[group]
  const { kind, ownerType, source } = groupConfig

  const { data: definitions = [], isLoading: isLoadingDefinitions } =
    useShopifyMetafieldDefinitionsQuery(ownerType)
  const { data: propertyKeys = [], isLoading: isLoadingProperties } =
    useShopifyLineItemPropertyKeysQuery(source === "lineItemProperties")
  const { data: productTags = [], isLoading: isLoadingProductTags } = useShopifyProductTagsQuery(
    source === "productTags",
  )
  const { data: orderTags = [], isLoading: isLoadingOrderTags } = useShopifyOrderTagsQuery(
    source === "orderTags",
  )
  const [searchQuery, setSearchQuery] = useState("")
  const normalizedSearchQuery = normalizeSearchValue(searchQuery)

  const options = useMemo(() => {
    const optionBuilderBySource: Record<MetafieldSource, () => ComboboxOption[]> = {
      definitions: () =>
        buildDefinitionOptions(definitions, entry.key, usedKeys, normalizedSearchQuery),
      productTags: () =>
        buildStringOptions(productTags, entry.key, usedKeys, normalizedSearchQuery),
      orderTags: () => buildStringOptions(orderTags, entry.key, usedKeys, normalizedSearchQuery),
      lineItemProperties: () =>
        buildStringOptions(propertyKeys, entry.key, usedKeys, normalizedSearchQuery),
    }

    return optionBuilderBySource[source]()
  }, [
    definitions,
    entry.key,
    normalizedSearchQuery,
    orderTags,
    productTags,
    propertyKeys,
    source,
    usedKeys,
  ])

  const selectedDefinition = useMemo(
    () => definitions.find((d) => d.key === entry.key),
    [definitions, entry.key],
  )

  function onCopyDescription() {
    if (!selectedDefinition || !isFilled(selectedDefinition.description)) return
    onChange({ description: selectedDefinition.description })
  }

  const isLoadingBySource: Record<MetafieldSource, boolean> = {
    definitions: isLoadingDefinitions,
    productTags: isLoadingProductTags,
    orderTags: isLoadingOrderTags,
    lineItemProperties: isLoadingProperties,
  }
  const isLoading = isLoadingBySource[source]

  const hasDescriptionError = isFilled(entry.key) && !isFilled(entry.description)
  const value =
    source === "definitions"
      ? getDefinitionValue(selectedDefinition, entry.key)
      : getStringValue(entry.key)
  const placeholderKey = isLoading ? "loading" : groupConfig.placeholderKey
  const canCopyDefinitionDescription = Boolean(
    source === "definitions" && selectedDefinition && isFilled(selectedDefinition.description),
  )
  const copy = useMemo(
    () => ({
      customValuePrefix: groupConfig.customValueLabelKey
        ? t(`${TRANSLATION_PREFIX}.${groupConfig.customValueLabelKey}`)
        : undefined,
      descriptionPlaceholder: t(`${TRANSLATION_PREFIX}.descriptionPlaceholder`),
      descriptionRequired: t(`${TRANSLATION_PREFIX}.descriptionRequired`),
      emptyPlaceholder: t(`${TRANSLATION_PREFIX}.noMatches`),
      fieldLabel: t(`${TRANSLATION_PREFIX}.field.${group}`),
      fillDescriptionFromMetafield: t(`${TRANSLATION_PREFIX}.fillDescriptionFromMetafield`),
      helperText: groupConfig.helperTextKey
        ? t(`${TRANSLATION_PREFIX}.${groupConfig.helperTextKey}`)
        : undefined,
      kindLabel: t(`${TRANSLATION_PREFIX}.kind.${kind}`),
      placeholder: t(`${TRANSLATION_PREFIX}.${placeholderKey}`),
      remove: t(`${TRANSLATION_PREFIX}.remove`),
      searchPlaceholder: t(`${TRANSLATION_PREFIX}.search`),
    }),
    [group, groupConfig.customValueLabelKey, groupConfig.helperTextKey, kind, placeholderKey, t],
  )
  const customValueLabel = useMemo(() => {
    if (!copy.customValuePrefix) return undefined
    return (query: string) => `${copy.customValuePrefix} "${query}"`
  }, [copy.customValuePrefix])

  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{copy.kindLabel}</Badge>
            <Badge variant="secondary">{copy.fieldLabel}</Badge>
          </div>
          <div className="flex items-center gap-1">
            {canCopyDefinitionDescription && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      onCopyDescription()
                      queueMicrotask(onBlur)
                    }}
                  >
                    <Import className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{copy.fillDescriptionFromMetafield}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{copy.remove}</TooltipContent>
            </Tooltip>
          </div>
        </div>
        <Combobox
          options={options}
          value={value}
          onChange={(opt) => {
            onChange({ key: opt?.value })
            queueMicrotask(onBlur)
          }}
          placeholder={copy.placeholder}
          emptyPlaceholder={copy.emptyPlaceholder}
          searchPlaceholder={copy.searchPlaceholder}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          shouldFilter={false}
          allowCustomValue={Boolean(groupConfig.customValueLabelKey)}
          customValueLabel={customValueLabel}
          helperText={copy.helperText}
        />
        <Textarea
          placeholder={copy.descriptionPlaceholder}
          value={entry.description}
          onChange={(e) => onChange({ description: e.target.value })}
          onBlur={onBlur}
          maxLength={SHOPIFY_METAFIELD_MAX_LENGTH}
          rows={2}
          className={
            hasDescriptionError ? "border-destructive focus-visible:ring-destructive" : undefined
          }
        />
        {hasDescriptionError && (
          <p className="text-xs text-destructive">{copy.descriptionRequired}</p>
        )}
      </CardContent>
    </Card>
  )
}
